/**
 * useComposer — complete state management for the post composer.
 *
 * Owns all mutable state: text, media, selected accounts, schedule,
 * platform-specific options, and the publish lifecycle.
 *
 * UI components receive only the slices they need so re-renders stay local.
 */

import { useCallback, useEffect, useReducer, useRef } from 'react';

import type { PlatformId } from '../config/platforms';
import { PLATFORM_CONFIGS } from '../config/platforms';
import {
  buildMockMedia,
  listConnectedAccounts,
  MOCK_POST_TEXT,
  MOCK_SELECTED_ACCOUNT_IDS,
  publishPost,
  retryPublish,
  saveDraft,
} from '../api/composer.api';
import type { MediaLibraryFile } from '../api/mediaTree.api';
import type {
  AllPlatformOptions,
  ComposerDraft,
  ComposerMedia,
  ComposerState,
  ComposerValidation,
  ConnectedAccount,
  PerPlatformResult,
  ScheduleOptions,
} from '../types/composer';

// ─── Default values ───────────────────────────────────────────────────────────

const DEFAULT_PLATFORM_OPTIONS: AllPlatformOptions = {
  facebook: { disableComments: false },
  instagram: { postType: 'feed', firstComment: '', location: '' },
  linkedin: { accountType: 'personal' },
  youtube: {
    title: '',
    description: '',
    tags: '',
    playlist: '',
    category: '',
    visibility: 'public',
    thumbnailFile: null,
    thumbnailPreviewUrl: null,
  },
  x: { threadMode: false, sensitiveContent: false },
  threads: {},
  tiktok: {},
  pinterest: {},
};

const DEFAULT_SCHEDULE: ScheduleOptions = {
  mode: 'now',
  scheduledAt: null,
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
};

// ─── Reducer ─────────────────────────────────────────────────────────────────

type Action =
  | { type: 'SET_TEXT'; text: string }
  | { type: 'ADD_MEDIA'; items: ComposerMedia[] }
  | { type: 'TOGGLE_LIBRARY_MEDIA'; file: MediaLibraryFile }
  | { type: 'REMOVE_MEDIA'; id: string }
  | { type: 'REORDER_MEDIA'; fromIndex: number; toIndex: number }
  | { type: 'UPDATE_MEDIA_PROGRESS'; id: string; percent: number }
  | { type: 'MEDIA_UPLOAD_DONE'; id: string }
  | { type: 'MEDIA_UPLOAD_ERROR'; id: string; error: string }
  | { type: 'TOGGLE_ACCOUNT'; id: string }
  | { type: 'SET_SCHEDULE'; schedule: ScheduleOptions }
  | { type: 'SET_PLATFORM_OPTIONS'; platform: PlatformId; options: Partial<AllPlatformOptions[PlatformId]> }
  | { type: 'SET_YOUTUBE_THUMBNAIL'; previewUrl: string | null; file: File | null }
  | { type: 'START_PUBLISH' }
  | { type: 'SET_PUBLISH_RESULTS'; results: PerPlatformResult[] }
  | { type: 'PUBLISH_DONE' }
  | { type: 'RESET' }
  | { type: 'LOAD_DRAFT'; draft: ComposerDraft };

function reducer(state: ComposerState, action: Action): ComposerState {
  switch (action.type) {
    case 'SET_TEXT':
      return { ...state, text: action.text };

    case 'ADD_MEDIA':
      return { ...state, media: [...state.media, ...action.items] };

    case 'TOGGLE_LIBRARY_MEDIA': {
      const existing = state.media.find((m) => m.libraryId === action.file.id);
      if (existing) {
        return { ...state, media: state.media.filter((m) => m.id !== existing.id) };
      }
      return { ...state, media: [...state.media, fromLibraryFile(action.file)] };
    }

    case 'REMOVE_MEDIA':
      return { ...state, media: state.media.filter((m) => m.id !== action.id) };

    case 'REORDER_MEDIA': {
      const media = [...state.media];
      const [moved] = media.splice(action.fromIndex, 1);
      media.splice(action.toIndex, 0, moved);
      return { ...state, media };
    }

    case 'UPDATE_MEDIA_PROGRESS':
      return {
        ...state,
        media: state.media.map((m) =>
          m.id === action.id ? { ...m, uploadPercent: action.percent } : m
        ),
      };

    case 'MEDIA_UPLOAD_DONE':
      return {
        ...state,
        media: state.media.map((m) =>
          m.id === action.id ? { ...m, uploaded: true, uploadPercent: 100 } : m
        ),
      };

    case 'MEDIA_UPLOAD_ERROR':
      return {
        ...state,
        media: state.media.map((m) =>
          m.id === action.id ? { ...m, uploadError: action.error } : m
        ),
      };

    case 'TOGGLE_ACCOUNT': {
      const ids = state.selectedAccountIds;
      return {
        ...state,
        selectedAccountIds: ids.includes(action.id)
          ? ids.filter((id) => id !== action.id)
          : [...ids, action.id],
      };
    }

    case 'SET_SCHEDULE':
      return { ...state, schedule: action.schedule };

    case 'SET_PLATFORM_OPTIONS':
      return {
        ...state,
        platformOptions: {
          ...state.platformOptions,
          [action.platform]: {
            ...state.platformOptions[action.platform],
            ...action.options,
          },
        },
      };

    case 'SET_YOUTUBE_THUMBNAIL':
      return {
        ...state,
        platformOptions: {
          ...state.platformOptions,
          youtube: {
            ...state.platformOptions.youtube,
            thumbnailFile: action.file,
            thumbnailPreviewUrl: action.previewUrl,
          },
        },
      };

    case 'START_PUBLISH':
      return { ...state, publishPhase: 'validating', publishResults: [] };

    case 'SET_PUBLISH_RESULTS':
      return { ...state, publishPhase: 'publishing', publishResults: action.results };

    case 'PUBLISH_DONE':
      return { ...state, publishPhase: 'done' };

    case 'RESET':
      return INITIAL_STATE;

    case 'LOAD_DRAFT':
      return {
        ...state,
        text: action.draft.text,
        selectedAccountIds: action.draft.selectedAccountIds,
        schedule: action.draft.schedule,
        platformOptions: {
          ...DEFAULT_PLATFORM_OPTIONS,
          ...action.draft.platformOptions,
        },
        editingDraftId: action.draft.id,
        publishPhase: 'idle',
        publishResults: [],
      };

    default:
      return state;
  }
}

/**
 * The composer opens on a filled-in post rather than an empty one. There is no
 * backend yet, and an empty composer shows none of the behaviour that matters —
 * per-platform counters, the preview, the summary. Delete this seed the moment
 * drafts load from the API.
 */
const INITIAL_STATE: ComposerState = {
  text: MOCK_POST_TEXT,
  media: buildMockMedia(),
  selectedAccountIds: MOCK_SELECTED_ACCOUNT_IDS,
  schedule: DEFAULT_SCHEDULE,
  platformOptions: DEFAULT_PLATFORM_OPTIONS,
  publishPhase: 'idle',
  publishResults: [],
  editingDraftId: null,
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export interface UseComposerReturn {
  // ── State ──────────────────────────────────────────────────────────────────
  state: ComposerState;
  accounts: ConnectedAccount[];
  accountsLoading: boolean;
  /** Derived: unique platforms from selected accounts. */
  selectedPlatforms: PlatformId[];
  /** Validation result (always up-to-date). */
  validation: ComposerValidation;

  // ── Text ───────────────────────────────────────────────────────────────────
  setText: (text: string) => void;

  // ── Media ──────────────────────────────────────────────────────────────────
  addFiles: (files: File[]) => void;
  removeMedia: (id: string) => void;
  toggleLibraryFile: (file: MediaLibraryFile) => void;
  /** Library IDs currently attached, for the picker's selected state. */
  attachedLibraryIds: string[];
  reorderMedia: (fromIndex: number, toIndex: number) => void;

  // ── Accounts ───────────────────────────────────────────────────────────────
  toggleAccount: (id: string) => void;

  // ── Schedule ───────────────────────────────────────────────────────────────
  setSchedule: (s: ScheduleOptions) => void;

  // ── Platform options ───────────────────────────────────────────────────────
  setPlatformOption: <P extends PlatformId>(
    platform: P,
    options: Partial<AllPlatformOptions[P]>
  ) => void;
  setYouTubeThumbnail: (file: File | null) => void;

  // ── Actions ────────────────────────────────────────────────────────────────
  publish: () => Promise<void>;
  retryAccount: (accountId: string) => Promise<void>;
  saveAsDraft: () => Promise<ComposerDraft>;
  reset: () => void;
  closePublishProgress: () => void;
}

export function useComposer(): UseComposerReturn {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const resultsRef = useRef<PerPlatformResult[]>([]);

  // ── Load connected accounts ─────────────────────────────────────────────
  const [accounts] = useStateWithLoading<ConnectedAccount[]>([], true);

  useEffect(() => {
    let cancelled = false;
    accounts.setLoading(true);
    void listConnectedAccounts()
      .then((list) => {
        if (!cancelled) accounts.setValue(list);
      })
      .finally(() => {
        if (!cancelled) accounts.setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // Accounts are fetched once per mount; nothing in this effect depends on
    // composer state.
  }, []);

  // ── Derived ─────────────────────────────────────────────────────────────
  const selectedPlatforms = useCallback((): PlatformId[] => {
    const selected = accounts.value.filter((a) => state.selectedAccountIds.includes(a.id));
    return [...new Set(selected.map((a) => a.platform))];
  }, [accounts.value, state.selectedAccountIds])();

  const attachedLibraryIds = state.media
    .map((m) => m.libraryId)
    .filter((id): id is string => id !== null);

  const validation = useCallback((): ComposerValidation => {
    const errors: ComposerValidation['errors'] = [];

    if (state.selectedAccountIds.length === 0) {
      errors.push({ field: 'platform', message: 'Select at least one account to publish to.' });
    }

    const platforms = selectedPlatforms;

    if (state.text.trim().length === 0 && !platforms.includes('youtube')) {
      errors.push({ field: 'text', message: 'Post text cannot be empty.' });
    }

    // Platform-specific char limit checks
    for (const pid of platforms) {
      const cfg = PLATFORM_CONFIGS[pid];
      if (state.text.length > cfg.charLimits.caption) {
        errors.push({
          field: `platform_${pid}`,
          message: `${cfg.name}: caption exceeds ${cfg.charLimits.caption.toLocaleString()} characters.`,
        });
      }
    }

    // YouTube requires a title
    if (platforms.includes('youtube') && !state.platformOptions.youtube.title.trim()) {
      errors.push({ field: 'platform_youtube', message: 'YouTube requires a video title.' });
    }

    // Schedule requires a date in the future
    if (
      state.schedule.mode === 'scheduled' &&
      (!state.schedule.scheduledAt || new Date(state.schedule.scheduledAt) <= new Date())
    ) {
      errors.push({ field: 'schedule', message: 'Schedule time must be in the future.' });
    }

    return { valid: errors.length === 0, errors };
  }, [state, selectedPlatforms])();

  // ── Text ────────────────────────────────────────────────────────────────
  const setText = useCallback((text: string) => dispatch({ type: 'SET_TEXT', text }), []);

  // ── Media ───────────────────────────────────────────────────────────────
  const addFiles = useCallback((files: File[]) => {
    const items: ComposerMedia[] = files.map((file) => ({
      id: crypto.randomUUID(),
      file,
      libraryId: null,
      name: file.name,
      sizeBytes: file.size,
      previewUrl: URL.createObjectURL(file),
      type: file.type.startsWith('video/') ? 'video' : 'image',
      width: null,
      height: null,
      durationMs: null,
      uploadPercent: 0,
      uploaded: false,
      uploadError: null,
    }));

    dispatch({ type: 'ADD_MEDIA', items });

    // Simulate upload for each file
    for (const item of items) {
      simulateUpload(item.id, dispatch);
    }
  }, []);

  const removeMedia = useCallback(
    (id: string) => dispatch({ type: 'REMOVE_MEDIA', id }),
    []
  );

  /** Attach a library asset, or detach it if it is already on the post. */
  const toggleLibraryFile = useCallback(
    (file: MediaLibraryFile) => dispatch({ type: 'TOGGLE_LIBRARY_MEDIA', file }),
    []
  );

  const reorderMedia = useCallback(
    (fromIndex: number, toIndex: number) =>
      dispatch({ type: 'REORDER_MEDIA', fromIndex, toIndex }),
    []
  );

  // ── Accounts ────────────────────────────────────────────────────────────
  const toggleAccount = useCallback(
    (id: string) => dispatch({ type: 'TOGGLE_ACCOUNT', id }),
    []
  );

  // ── Schedule ────────────────────────────────────────────────────────────
  const setSchedule = useCallback(
    (schedule: ScheduleOptions) => dispatch({ type: 'SET_SCHEDULE', schedule }),
    []
  );

  // ── Platform options ────────────────────────────────────────────────────
  const setPlatformOption = useCallback(
    <P extends PlatformId>(platform: P, options: Partial<AllPlatformOptions[P]>) =>
      dispatch({
        type: 'SET_PLATFORM_OPTIONS',
        platform,
        options: options as Partial<AllPlatformOptions[PlatformId]>,
      }),
    []
  );

  const setYouTubeThumbnail = useCallback((file: File | null) => {
    const previewUrl = file ? URL.createObjectURL(file) : null;
    dispatch({ type: 'SET_YOUTUBE_THUMBNAIL', previewUrl, file });
  }, []);

  // ── Publish ─────────────────────────────────────────────────────────────
  const publish = useCallback(async () => {
    if (!validation.valid) return;
    dispatch({ type: 'START_PUBLISH' });

    const results = await publishPost(
      state.selectedAccountIds,
      state.text,
      (updated) => {
        resultsRef.current = updated;
        dispatch({ type: 'SET_PUBLISH_RESULTS', results: updated });
      }
    );

    resultsRef.current = results;
    dispatch({ type: 'PUBLISH_DONE' });
  }, [state.selectedAccountIds, state.text, validation.valid]);

  const retryAccount = useCallback(
    async (accountId: string) => {
      const updated = await retryPublish(accountId, resultsRef.current, (r) => {
        resultsRef.current = r;
        dispatch({ type: 'SET_PUBLISH_RESULTS', results: r });
      });
      resultsRef.current = updated;
      const allDone = updated.every((r) => r.status === 'success' || r.status === 'failed');
      if (allDone) dispatch({ type: 'PUBLISH_DONE' });
    },
    []
  );

  const saveAsDraft = useCallback(async (): Promise<ComposerDraft> => {
    return saveDraft({
      id: state.editingDraftId ?? undefined,
      text: state.text,
      mediaNames: state.media.map((m) => m.name),
      selectedAccountIds: state.selectedAccountIds,
      schedule: state.schedule,
      platformOptions: state.platformOptions,
    });
  }, [state]);

  const reset = useCallback(() => dispatch({ type: 'RESET' }), []);

  const closePublishProgress = useCallback(() => dispatch({ type: 'RESET' }), []);

  return {
    state,
    accounts: accounts.value,
    accountsLoading: accounts.loading,
    selectedPlatforms,
    validation,
    setText,
    addFiles,
    removeMedia,
    toggleLibraryFile,
    attachedLibraryIds,
    reorderMedia,
    toggleAccount,
    setSchedule,
    setPlatformOption,
    setYouTubeThumbnail,
    publish,
    retryAccount,
    saveAsDraft,
    reset,
    closePublishProgress,
  };
}

// ─── Internal helpers ─────────────────────────────────────────────────────────

/** Tiny hook-like object that avoids a second useReducer just for loading state. */
function useStateWithLoading<T>(initial: T, initialLoading: boolean) {
  const [combined, setCombined] = useReducer(
    (
      prev: { value: T; loading: boolean },
      next: Partial<{ value: T; loading: boolean }>
    ) => ({ ...prev, ...next }),
    { value: initial, loading: initialLoading }
  );
  return [
    {
      value: combined.value,
      loading: combined.loading,
      setValue: (value: T) => setCombined({ value }),
      setLoading: (loading: boolean) => setCombined({ loading }),
    },
  ] as const;
}

/**
 * Library assets are already stored, so they skip the upload lifecycle entirely
 * and land attached and complete.
 */
function fromLibraryFile(file: MediaLibraryFile): ComposerMedia {
  return {
    id: `lib-${file.id}`,
    file: null,
    libraryId: file.id,
    name: file.name,
    sizeBytes: file.sizeBytes,
    previewUrl: file.previewUrl,
    type: file.type,
    width: file.width,
    height: file.height,
    durationMs: file.durationMs,
    uploadPercent: 100,
    uploaded: true,
    uploadError: null,
  };
}

/** Simulates multi-step S3 upload with progress ticks. */
function simulateUpload(id: string, dispatch: React.Dispatch<Action>): void {
  const steps = 12;
  let current = 0;

  const tick = () => {
    current += 1;
    const percent = Math.round((current / steps) * 100);
    dispatch({ type: 'UPDATE_MEDIA_PROGRESS', id, percent });

    if (current < steps) {
      setTimeout(tick, 120 + Math.random() * 80);
    } else {
      dispatch({ type: 'MEDIA_UPLOAD_DONE', id });
    }
  };

  setTimeout(tick, 50);
}
